import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, defer, throwError } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  ApiHttpError,
  getFriendlyMessage,
  isProblemDetailsBody,
  mapHttpStatusToEnum,
  resolveProblemMessage,
} from './api-http-error.model';
import { buildApiUrl } from './api-url.util';

@Injectable({
  providedIn: 'root',
})
export class HttpClientService {
  constructor(private readonly httpClient: HttpClient) {}

  get<T>(path: string,
    options?: { params?: HttpParams | Record<string, string>; apiUrl?: string },
  ): Observable<T> {
    return defer(() =>
      this.request(
        this.httpClient.get<T>(this.buildUrl(path, options?.apiUrl), { params: options?.params }),
      ),
    );
  }

  /**
   * Download de arquivo: a resposta vem como `Blob`, não como JSON. Num erro o corpo também
   * chega como Blob, então a mensagem sai do status — o `ProblemDetails` não é legível aqui.
   */
  getBlob(path: string, options?: { params?: HttpParams | Record<string, string> }): Observable<Blob> {
    return defer(() =>
      this.request(
        this.httpClient.get(this.buildUrl(path), {
          params: options?.params,
          responseType: 'blob',
        }),
      ),
    );
  }

  post<T>(
    path: string,
    body: unknown,
    options?: { params?: HttpParams | Record<string, string>; apiUrl?: string },
  ): Observable<T> {
    return defer(() =>
      this.request(
        this.httpClient.post<T>(this.buildUrl(path, options?.apiUrl), body, { params: options?.params }),
      ),
    );
  }

  put<T>(path: string, body: unknown): Observable<T> {
    return defer(() => this.request(this.httpClient.put<T>(this.buildUrl(path), body)));
  }

  patch<T>(path: string, body: unknown): Observable<T> {
    return defer(() => this.request(this.httpClient.patch<T>(this.buildUrl(path), body)));
  }

  delete<T = void>(path: string): Observable<T> {
    return defer(() => this.request(this.httpClient.delete<T>(this.buildUrl(path))));
  }

  private request<T>(source: Observable<T>): Observable<T> {
    return source.pipe(catchError((error: unknown) => this.handleError(error)));
  }

  private buildUrl(path: string, apiUrl = environment.apiParametrizacaoUrl): string {
    if (!environment.baseUrl?.trim() && !apiUrl?.trim()) {
      throw new ApiHttpError(
        mapHttpStatusToEnum(0),
        'API base URL is not configured (baseUrl and API segment are empty).',
      );
    }

    return buildApiUrl(path, apiUrl);
  }

  private handleError(error: unknown): Observable<never> {
    if (error instanceof ApiHttpError) {
      return throwError(() => error);
    }

    if (!(error instanceof HttpErrorResponse)) {
      return throwError(
        () =>
          new ApiHttpError(
            mapHttpStatusToEnum(0),
            error instanceof Error ? error.message : 'Unexpected HTTP error.',
          ),
      );
    }

    const status = mapHttpStatusToEnum(error.status);
    const problem = isProblemDetailsBody(error.error) ? error.error : undefined;
    const message = resolveProblemMessage(problem, getFriendlyMessage(error.status));

    return throwError(() => new ApiHttpError(status, message, problem));
  }
}
